import React, { FC, useRef, useState } from "react";
import { withProcedureWraper } from "../..";
import { Box } from "../../../../components/box";
import { Button } from "react-bootstrap";
import EditDepartmentDetailModal from "../modal/edit-department-detail-modal";
import { useDocDepartments } from "./hooks/use-doc-departments";
import { Input, InputRef, Space, Spin, Button as AntButton, Tag } from "antd";
import { FontFa, Table } from "../../../../components";
import { ColumnsType } from "antd/lib/table";
import { ColumnType } from "antd/es/table";
import { FilterConfirmProps } from "antd/lib/table/interface";
import { useDocumentType } from "../../../document/hooks";
import WarningDeleteModal from "../../../../modals/warning-delete-modal";
import { DepartmentService } from "../../../../services/lookup-data/department.service";
import { useQueryClient } from "@tanstack/react-query";
import { CreateNotification } from "../../../../components/notifier";

type DocDepartmentType = {
  Id: string;
  CompanyId: string;
  CompanyName: string;
  Abbr: string;
  DeptCode: string;
  Name: string;
  Status: number;
  children: DocDepartmentType[];
};

const getDescendantValues = (record: any, dataIndex: any) => {
  const values = [];
  (function recurse(record) {
    values.push(record[dataIndex]?.toString().toLowerCase());
    record?.children?.forEach(recurse);
  })(record);
  return values;
};

export const SettingDocumentDepartmentPage: FC = withProcedureWraper(() => {
  const [currentData, setCurrentData] = useState<any>();
  const [openEditDepartmentDetailModal, setOpenEditDepartmentDetailModal] = useState(false);
  const { isLoading, departmentsTreeData } = useDocDepartments();
  const searchInput = useRef<InputRef>(null);
  const { getDocumentTypeLabel } = useDocumentType();
  const [deleteTitle, setDeleteTitle] = useState("");
  const [deleteMessage, setDeleteMessage] = useState("");
  const [showDeletedConfirm, setShowDeletedConfirm] = useState(false);
  const queryClient = useQueryClient();

  const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: any) => {
    confirm();
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
  };

  const getColumnSearchProps = (dataIndex: any, placeholder?: string): ColumnType<any> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <AntButton
            type="primary"
            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
            icon={<FontFa name="faSearch" />}
            size="small"
            style={{ width: 90 }}
          >
            Tìm kiếm
          </AntButton>
          <AntButton onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>
            Reset
          </AntButton>
          <AntButton
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            Đóng
          </AntButton>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => <FontFa name="faSearch" size="1x" color="#333" />,
    onFilter: (value, record) => {
      const recordName = record[dataIndex];
      const searchLower = value.toString().toLowerCase();
      return (
        recordName?.toString()?.toLowerCase()?.includes(searchLower) ||
        getDescendantValues(record, dataIndex).some((descValue) => descValue?.includes(searchLower))
      );
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const handleDeleteConfirmDialog = async (row: any) => {
    setCurrentData(row);
    setDeleteTitle(row.CompanyId ? "Xóa đơn vị ban hành" : "Xóa công ty");
    let message = `Bạn có muốn xóa đơn vị ban hành '${row.Name}' không?`;
    if (!row.CompanyId) {
      message =
        row.children.length > 0
          ? `Bạn có muốn xóa công ty '${row.Name || ""}' và những đơn vị ban hành của nó không?`
          : `Bạn có muốn xóa công ty '${row.Name || ""}' không?`;
    }
    setDeleteMessage(message);
    setShowDeletedConfirm(true);
  };

  const handleShowDetailForm = async (row: any, parentRow: any) => {
    let data: any = null;
    if (!row) {
      data = {
        CompanyId: parentRow?.Id,
        CompanyName: parentRow?.Name,
        docType: parentRow?.docType,
      };
    } else {
      data = { ...row };
    }

    setCurrentData(data);
    setOpenEditDepartmentDetailModal(!openEditDepartmentDetailModal);
  };

  const handleDeleteData = async (data: any) => {
    await DepartmentService.delete(data.Id);
    if (data.children?.length > 0) {
      data.children.forEach(async (item: any) => {
        await DepartmentService.delete(item.Id);
      });
    }
    queryClient.invalidateQueries({ queryKey: ["doc-departments"] });
    CreateNotification({ title: "Thành công", body: "Xoá thành công!", icon: "/icons/success.png" });
  };

  const toggleEditDepartmentDetailModal = () => {
    setOpenEditDepartmentDetailModal(!openEditDepartmentDetailModal);
  };

  const columns: ColumnsType<DocDepartmentType> = [
    {
      title: "Tên Đơn Vị Ban Hành",
      dataIndex: "Name",
      key: "Name",
      ...getColumnSearchProps("Name", "Nhập tên đơn vị ban hành"),
    },
    {
      title: "Mã Hiệu",
      dataIndex: "DeptCode",
      key: "DeptCode",
      width: 150,
      ...getColumnSearchProps("DeptCode", "Nhập mã hiệu"),
    },
    {
      title: "Tên Viết Tắt",
      dataIndex: "Abbr",
      key: "Abbr",
      width: 150,
      ...getColumnSearchProps("Abbr", "Nhập tên viết tắt"),
    },
    {
      title: "Loại tài liệu",
      dataIndex: "docType",
      key: "docType",
      width: 150,
      render: (docType: string[]) => docType.map((e) => <Tag>{getDocumentTypeLabel(e)}</Tag>),
    },
    {
      title: "Tình Trạng",
      dataIndex: "Status",
      key: "Status",
      width: 150,
      render: (status) => (status === 1 ? "Đang sử dụng" : "Chưa sử dụng"),
      filters: [
        {
          text: "Đang sử dụng",
          value: 1,
        },
        {
          text: "Chưa sử dụng",
          value: 2,
        },
      ],
      onFilter: (value: any, record) => record?.Status?.toString().indexOf(value) === 0,
    },
    {
      dataIndex: "action",
      width: 180,
      render: (_, record) => (
        <Space>
          {!record.CompanyId ? (
            <Button
              title="Thêm đơn vị ban hành"
              onClick={(e) => {
                e.stopPropagation();
                handleShowDetailForm(null, record);
              }}
              className="m-1 text-success"
              variant="outline"
            >
              <FontFa name="faPlus" size="sm" />
            </Button>
          ) : (
            <></>
          )}
          <Button
            title="Chỉnh sửa"
            onClick={(e) => {
              e.stopPropagation();
              handleShowDetailForm(record, null);
            }}
            className={record.CompanyId ? "ml-5 text-info" : "text-info"}
            variant="outline"
          >
            <FontFa name="faEdit" size="sm" />
          </Button>
          <Button
            title="Xóa"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteConfirmDialog(record);
            }}
            className="m-1 text-danger"
            variant="outline"
          >
            <FontFa name="faTrash" size="sm" />
          </Button>
        </Space>
      ),
    },
  ];

  if (isLoading) return <Spin />;

  return (
    <>
      <div className="ProcedureTrainingPlanPage">
        <div className="pd15">
          <div className="d-flex p-3 justify-content-center">
            <h3>
              <br />
            </h3>
          </div>
          <Box
            title={"Quản lý bộ phận ban hành"}
            titleRight={
              <div className="px-4 py-2 d-flex align-items-center">
                <Button
                  className="mr-2 button-info"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShowDetailForm(null, null);
                  }}
                  variant="info"
                >
                  {"Thêm công ty"}
                </Button>
              </div>
            }
          >
            <Table
              columns={columns}
              dataSource={departmentsTreeData}
              expandable={{ defaultExpandAllRows: true }}
              pagination={{ pageSize: 1000 }}
              bordered
              scroll={{ y: 500 }}
            />
          </Box>
          <EditDepartmentDetailModal
            onClose={toggleEditDepartmentDetailModal}
            isOpen={openEditDepartmentDetailModal}
            data={currentData}
          />
          <WarningDeleteModal
            data={currentData}
            title={deleteTitle}
            message={deleteMessage}
            isOpen={showDeletedConfirm}
            onDelete={handleDeleteData}
            onClose={() => setShowDeletedConfirm(!showDeletedConfirm)}
            basis="180px"
          />
        </div>
      </div>
    </>
  );
});
